"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * EPIC-029 STORY-1716 Part 3 — first-deposit currency-display
 * educational tooltip.
 *
 * Copy revised 2026-05-08 (CLAUDE.md "BR-primary display flip"
 * entry / EPIC-029 follow-up Story 5). Earlier copy framed USDC
 * as primary with BRL as a projection ("Seu saldo é em USDC. O
 * valor em reais é apenas uma projeção…"); the flip leads with
 * the user's local fiat as the canonical unit and surfaces the
 * USDC-under-the-hood mechanism + platform fx spread together.
 * Updated copy:
 *
 *   pt-BR baseline — "Seu saldo é em USDC, exibido em R$ pelo
 *   câmbio do dia (com spread Granbay). O valor pode variar
 *   entre depósito e saque."
 *
 *   en baseline — "Your balance is held in USDC. We show local-
 *   currency values using today's exchange rate plus a small
 *   platform spread, so the amount you receive can differ
 *   slightly between deposit and withdrawal."
 *
 * Deep-link to the full help-center article (`/help/usdc-and-brl-
 * display`, PR #1053) for users who want the long-form rationale.
 *
 * Reusable on /withdraw too — the same FX-risk-on-user message
 * applies. Caller passes `surface` to disambiguate the storage
 * key so a deposit-side dismissal doesn't suppress the withdraw-
 * side notice (admins may want the user to re-acknowledge in the
 * other direction).
 *
 * SSR-safe: defers all `localStorage` access to a useEffect so
 * the initial render is identical for guest + authed users.
 */

const STORAGE_KEY_PREFIX = "granbay.usdc_tooltip_dismissed";

interface UsdcEducationalTooltipProps {
  /**
   * Disambiguates the dismissal key. `'deposit'` and `'withdraw'`
   * each track their own seen-state. Other surfaces can pass any
   * stable string.
   */
  surface: "deposit" | "withdraw";
}

export function UsdcEducationalTooltip({
  surface,
}: UsdcEducationalTooltipProps) {
  const { t, i18n } = useTranslation();
  const [show, setShow] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const dismissed = window.localStorage.getItem(
        `${STORAGE_KEY_PREFIX}.${surface}`,
      );
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShow(dismissed !== "1");
    } catch {
      // localStorage blocked (e.g. private mode) — show the tooltip
      // every time. Cheap UX cost; better than crashing.
       
      setShow(true);
    }
     
    setHydrated(true);
  }, [surface]);

  function handleDismiss() {
    setShow(false);
    try {
      window.localStorage.setItem(`${STORAGE_KEY_PREFIX}.${surface}`, "1");
    } catch {
      // ignored — see useEffect comment.
    }
  }

  // SSR + pre-hydration: render nothing so the server-rendered HTML
  // matches what the client emits before the localStorage check runs.
  if (!hydrated || !show) return null;

  // Locale-aware learn-more deep-link. The help-center catalog
  // routes the same slug for both en and pt; the catalog body picks
  // the locale at render time.
  const learnMoreHref = "/help/usdc-and-brl-display";

  // Pull copy from i18n catalog with hardcoded pt-BR / en fallbacks.
  // Copy revised 2026-05-08 BR-primary flip — see component docstring
  // for the founder's verbatim spec.
  const isPt =
    i18n.language?.startsWith("pt") ?? false;
  const fallback = isPt
    ? {
        title: "Seu saldo é exibido em R$",
        body:
          "Seu saldo é em USDC, exibido em R$ pelo câmbio do dia (com spread Granbay). O valor pode variar entre depósito e saque.",
        learnMore: "Saiba mais",
        dismiss: "Entendi",
      }
    : {
        title: "Your balance is shown in your local currency",
        body:
          "Your balance is held in USDC. We show local-currency values using today's exchange rate plus a small platform spread, so the amount you receive can differ slightly between deposit and withdrawal.",
        learnMore: "Learn more",
        dismiss: "Got it",
      };

  return (
    <div
      role="status"
      aria-live="polite"
      className="mb-3 rounded-md border border-dashed p-3 text-[12px]"
      style={{
        borderColor: "var(--color-brand)",
        background: "var(--color-brand-tint)",
        color: "var(--color-text2)",
      }}
    >
      <div className="mb-1 font-semibold text-[var(--color-text)]">
        {t("usdcEducationalTooltip.title", fallback.title)}
      </div>
      <p>{t("usdcEducationalTooltip.body", fallback.body)}</p>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <Link
          href={learnMoreHref}
          className="underline hover:text-[var(--color-text)]"
        >
          {t("usdcEducationalTooltip.learnMore", fallback.learnMore)}
        </Link>
        <button
          type="button"
          onClick={handleDismiss}
          className="rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-0.5 text-[11px] text-[var(--color-text2)] hover:text-[var(--color-text)]"
        >
          {t("usdcEducationalTooltip.dismiss", fallback.dismiss)}
        </button>
      </div>
    </div>
  );
}
